import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 3.5 visual preview at /preview/lesson/3.5
 * Week 3 capstone: "Run the chain end to end on one real assignment"
 * 8-section shape like 2.5.
 */

export default function LessonPreviewPage3_5() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-brief',
  'sec-chain',
  'sec-stage-1',
  'sec-stage-2',
  'sec-stage-3',
  'sec-stage-4',
  'sec-forward',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 8;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[210px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <Brief />
          <Chain />
          <Stage1 />
          <Stage2 />
          <Stage3 />
          <Stage4 />
          <CarryForward />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 3 · Building with AI · Lesson 3.5 · Capstone
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        Run the chain on one real thing
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 55 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Chain · Judgment · Character · Voice</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        No new vocab. One assignment. Every move from 3.1 to 3.4.
      </p>
    </header>
  );
}

/* --- Section 1: Brief --- */
function Brief() {
  return (
    <Section id="sec-brief" kicker="The brief" title="One assignment. Two pages. Real stakes.">
      <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          Assignment
        </p>
        <h3 className="mt-2 text-xl font-semibold text-text leading-snug">
          Write a 2-page argument essay on whether US high schools should require a
          financial literacy course to graduate.
        </h3>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <BriefRow label="Length" value="Two pages. Roughly 700 to 800 words." />
          <BriefRow label="Due" value="Friday. You are starting on Monday night." />
          <BriefRow label="Sources" value="Minimum three. Cited inline." />
          <BriefRow label="Position" value="You pick. Defend it." />
        </div>
      </div>

      <p className="mt-6 text-text/90">
        This is not a hypothetical. By the end of this lesson you will have done
        every move on this real assignment. Research moves. Drafting moves. Audit
        moves. Ship moves. The output is the kind of work the rest of school is
        actually about.
      </p>
      <p className="mt-4 text-text/90">
        We are not going to write the essay for you. We are going to run the chain
        with you, stage by stage, on this specific brief. You do the work in your
        own notebook or doc as we go. By the end you have a draft that is yours and
        that you would defend.
      </p>

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Before you read on
        </p>
        <p className="mt-3 text-text/90">
          Pick a position right now. Required or not required. Do not look anything
          up. Do not ask AI. Just decide what you currently think. Write it as one
          sentence. The chain starts with you having a take, not with AI giving you
          one.
        </p>
      </div>
    </Section>
  );
}

function BriefRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
        {label}
      </p>
      <p className="mt-1.5 text-sm text-text/90">{value}</p>
    </div>
  );
}

/* --- Section 2: The chain --- */
function Chain() {
  return (
    <Section id="sec-chain" kicker="The chain" title="Four links. In order. No skipping.">
      <p className="mt-6 text-text/90">
        The whole week comes down to this picture. Each link feeds the next. Each
        link has its own move. If you skip a link, the next one is broken.
      </p>
      <ChainDiagram />
      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          The handoff rule
        </p>
        <p className="mt-3 text-text/90">
          The output of one link is the input to the next. If you start drafting
          before research is done, you draft on hunches. If you start auditing
          before drafting is done, you audit nothing. If you ship before auditing
          is done, you ship something you cannot defend. The order is the move.
        </p>
      </div>
    </Section>
  );
}

const LINKS = [
  {
    n: '01',
    name: 'Research',
    lesson: '3.2',
    accent: 'emerald' as const,
    output: 'A short list of primary sources you have actually opened.',
  },
  {
    n: '02',
    name: 'Draft',
    lesson: '3.3',
    accent: 'amber' as const,
    output: 'A messy first draft where every sentence sounds like you.',
  },
  {
    n: '03',
    name: 'Audit',
    lesson: '3.4',
    accent: 'emerald' as const,
    output: 'A clean draft where every claim has a source and every paragraph sounds like you out loud.',
  },
  {
    n: '04',
    name: 'Ship',
    lesson: '3.5',
    accent: 'amber' as const,
    output: 'A submitted essay you would defend line by line on Monday.',
  },
];

function ChainDiagram() {
  return (
    <div className="mt-6 grid gap-3 sm:grid-cols-2">
      {LINKS.map((l) => {
        const isEm = l.accent === 'emerald';
        const tagCls = isEm
          ? 'bg-emerald-500/15 text-emerald-300'
          : 'bg-amber/15 text-amber';
        return (
          <div key={l.n} className="rounded-lg border border-line bg-surface p-4">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-mono text-[10px] text-muted">{l.n}</span>
              <span
                className={`rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${tagCls}`}
              >
                {l.name}
              </span>
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-muted">
              From {l.lesson}
            </p>
            <p className="mt-2 text-sm text-text/85">
              <span className="block font-mono text-[10px] uppercase tracking-wider text-amber">
                Output
              </span>
              {l.output}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 3: Stage 1 Research --- */
function Stage1() {
  return (
    <Section id="sec-stage-1" kicker="Stage 1 · Research" title="Find what is true on this question.">
      <p className="mt-6 text-text/90">
        You have a take. Now find out whether the world agrees with you or not. The
        move is not to ask AI &ldquo;is financial literacy required.&rdquo; The move
        is to ask AI where to look. Then go look.
      </p>

      <StageBlock
        label="The prompt you give AI"
        accent="emerald"
        body="I am writing an argument essay on whether US high schools should require a financial literacy course to graduate. Give me five primary sources I should open: state policy databases, peer-reviewed studies, government data, position statements from teachers unions or financial education groups. For each one, give me the name of the source, what it actually shows, and the most likely URL where I can find it. Do not summarize the source. Just point me at it."
      />

      <StageBlock
        label="What AI gives back · The three buckets"
        accent="amber"
        body="Some of what AI says will be real (training data on widely cited reports like Champlain College’s Financial Literacy report card). Some will come from search if your tool searches (the current state-by-state requirement map). Some will be invented (a study that does not exist with a perfectly real-sounding citation). Your job from 3.2: figure out which is which."
      />

      <StageBlock
        label="The audit move you do right here"
        accent="emerald"
        body="Open each source AI named. If the source does not open, cross it out. If the source opens but the claim AI attached is not on the page, cross out the claim. Keep three sources you have actually read. That is your input to the draft stage. Not five sources you skimmed. Three you opened."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <ul className="mt-3 space-y-2 text-sm text-text/90">
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            Your one-sentence position from the brief, possibly revised after
            reading.
          </li>
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            Three primary sources you opened, with the one specific claim from
            each that you will use.
          </li>
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            A short list of counter-arguments. The strongest version of the other
            side, also sourced.
          </li>
        </ul>
      </div>
    </Section>
  );
}

/* --- Section 4: Stage 2 Draft --- */
function Stage2() {
  return (
    <Section id="sec-stage-2" kicker="Stage 2 · Draft" title="Get words on the page. Keep your voice.">
      <p className="mt-6 text-text/90">
        Research is done. You have a position, three opened sources, and a
        counter-argument. Now write. The trap here is letting AI draft for you. The
        move is drafting with AI in the three modes from 3.3.
      </p>

      <StageBlock
        label="Blank-page mode · The opening"
        accent="amber"
        body="Write your own opening sentence first. If you are stuck, ask AI for three possible openings: a story, a stat, a question. Do not use any of them. Pick the one closest to where you want to start, then rewrite the actual opening in your own words. Your opening should be a sentence you would say out loud to a friend at lunch about why this matters."
      />

      <StageBlock
        label="Expander mode · The body paragraphs"
        accent="emerald"
        body="For each of your three sources, write one bullet that states what the source shows and why it supports your position. Paste the bullet into AI and ask for two or three different paragraph shapes that could expand it. Pick the shape that fits. Then write the paragraph yourself, with the actual claim from the actual source you opened, in your own words. Your bullet, your final paragraph, AI showed you a shape."
      />

      <StageBlock
        label="Polish mode · After the draft is done"
        accent="amber"
        body="Once you have a complete rough draft (opening + three body paragraphs + counter-argument + close), run polish mode on it paragraph by paragraph. Tighten without changing voice. Reject any change that erases your word choices or sentence rhythm. Re-type every change you accept by hand."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A complete first draft. Roughly the right length. Every paragraph
          something you wrote, in your voice, defending your position with the
          three sources you actually opened. Rough at the edges. That is fine.
          Audit is next.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 5: Stage 3 Audit --- */
function Stage3() {
  return (
    <Section id="sec-stage-3" kicker="Stage 3 · Audit" title="Two passes. Facts first. Voice second.">
      <p className="mt-6 text-text/90">
        Your draft is done. It is rough. It is yours. Now run the two-pass audit
        from 3.4 on this specific draft.
      </p>

      <StageBlock
        label="Pass 1 · Fact audit"
        accent="emerald"
        body="Highlight every claim. Every name. Every number. Every quote. Every URL. For each one, open the source. Find the line on the page. Confirm the claim is on the page. If a claim cannot be confirmed, cut it or replace it with one you can. The stat about how many states require financial literacy might have drifted since AI was trained: re-confirm on a current source. The Champlain report card grade for a specific state changes year to year: confirm the year."
      />

      <StageBlock
        label="Pass 2 · Voice audit"
        accent="amber"
        body="Read the whole draft out loud at normal speed. Stop on any sentence that does not sound like you. Rewrite it. Pay attention to: words you would never actually say, sentence rhythms that go medium-medium-medium, paragraphs that end on a tidy summary line that sounds like a textbook. Those are the AI tells. If a whole paragraph fails, rewrite the whole paragraph. The polished version is allowed to feel less polished. Polish is not the goal. Defendable is the goal."
      />

      <StageBlock
        label="The shippable test · Three yeses"
        accent="emerald"
        body="Can I name the source of every claim from memory right now. Can I explain any random paragraph without re-reading it. Does this sound like me out loud. If any answer is no, keep working. Do not submit on two yeses."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A draft that passed both audits. Every claim has a source you opened.
          Every paragraph sounds like you when you read it out loud. You can
          explain any random sentence without re-reading. This draft is shippable.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 6: Stage 4 Ship --- */
function Stage4() {
  return (
    <Section id="sec-stage-4" kicker="Stage 4 · Ship" title="Submit. Then close the loop.">
      <p className="mt-6 text-text/90">
        Shipping is not just hitting submit. Shipping is hitting submit and then
        doing two small things that make the next chain better. Most students skip
        them. The students who do not skip them get better every week.
      </p>

      <StageBlock
        label="Right before submit · The cooling-off pass"
        accent="amber"
        body="If you have time, close the doc. Walk away for ten minutes. Come back and read it cold. Anything that feels off when you have not seen the words for ten minutes is what a teacher will notice. Fix what jumps out. If you do not have time, this is fine. Three yeses on the shippable test is enough."
      />

      <StageBlock
        label="At submit · One sentence you keep"
        accent="emerald"
        body="Before you submit, write one sentence in your notes: what was hardest about this chain. The research step where one source did not exist. The draft step where you kept reaching for an AI word. The audit step where you cut a stat you liked. The sentence is for you, not for the teacher. The point is to notice the pattern of where you almost shipped something broken."
      />

      <StageBlock
        label="After the grade comes back"
        accent="amber"
        body="When you get the grade, look for whether the teacher pointed at anything specific. A fact that was wrong. A paragraph that did not sound like you. A claim that was unsupported. That feedback is more valuable than the grade. The next chain should not repeat that exact mistake."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A submitted essay. A note about what was hardest. A plan for what you do
          differently next time. This is what a real builder does after they ship.
          The chain ends with shipping, then immediately starts again.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 7: What you carry forward --- */
function CarryForward() {
  return (
    <Section id="sec-forward" kicker="What you carry forward" title="Three habits to keep. Three traps to remember.">
      <div className="mt-6 grid gap-6 sm:grid-cols-2">
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            Habits to keep
          </p>
          <ul className="mt-4 space-y-3 text-sm text-text/90">
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">01</span>
              Always open the source before you cite it. No exceptions.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">02</span>
              Re-type AI suggestions by hand. Never paste.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">03</span>
              Read the final draft out loud before you submit.
            </li>
          </ul>
        </div>
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            Traps to remember
          </p>
          <ul className="mt-4 space-y-3 text-sm text-text/90">
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">01</span>
              The perfectly-shaped citation that does not actually exist. Always
              check the URL.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">02</span>
              The paragraph that sounds smarter than you. That is the paragraph a
              teacher will point at.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">03</span>
              &ldquo;Done&rdquo; is not the same as &ldquo;shippable.&rdquo; Run
              the shippable test or you are gambling.
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          The bigger move
        </p>
        <p className="mt-3 text-text/90">
          This chain is how real work happens outside of school too. Research,
          draft, audit, ship. It is how good lawyers write briefs, how good
          engineers ship code, how good journalists write articles. You are not
          learning a school trick. You are learning the move that adults use to
          ship work they can defend. The earlier you build the habit, the further
          ahead you start.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 8: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card · Week 3" title="Screenshot this. The whole week on one card.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Week 3 · Lock card · Building with AI
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          Research, draft, audit, ship. In that order. No skipping. Every move
          checkable. Every sentence defendable.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <ChainTile n="01" name="Research" lesson="3.2" body="Ask AI where to look. Then go look. Citation in your paper is the thing AI pointed at, not AI." />
          <ChainTile n="02" name="Draft" lesson="3.3" body="Draft with AI. Do not let AI draft for you. Every sentence has to pass through your brain." />
          <ChainTile n="03" name="Audit" lesson="3.4" body="Two passes before submit. Facts first. Authorship second. Shippable, not just done." />
          <ChainTile n="04" name="Ship" lesson="3.5" body="Submit. Note what was hardest. Read the feedback when it comes. Run the chain again." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The Week 3 vocab spine
          </p>
          <p className="mt-3 text-text/90">
            <span className="font-mono text-[10px] text-amber mr-2">3.1</span>
            Chaining ·
            <span className="font-mono text-[10px] text-amber mx-2">3.2</span>
            Primary source ·
            <span className="font-mono text-[10px] text-amber mx-2">3.3</span>
            Voice fingerprint ·
            <span className="font-mono text-[10px] text-amber mx-2">3.4</span>
            Shippable
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          AI moves the work faster. AI does not decide what is true, write in your
          voice, or defend what you submit. Those are your job. That is the whole
          point of the chain.
        </p>
      </div>
    </Section>
  );
}

function ChainTile({
  n,
  name,
  lesson,
  body,
}: {
  n: string;
  name: string;
  lesson: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
        <span className="font-mono text-[10px] text-muted">Lesson {lesson}</span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Week 4
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            Doing the work AI cannot do
          </h3>
          <p className="mt-3 text-sm text-text/85">
            The chain works because some moves only you can make: deciding what
            matters, noticing what is specific, defending what you shipped. Week 4
            zooms in on those moves and what makes them hard to fake.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Stage helper --- */
function StageBlock({
  label,
  body,
  accent,
}: {
  label: string;
  body: string;
  accent: 'emerald' | 'amber';
}) {
  const isEm = accent === 'emerald';
  const borderCls = isEm ? 'border-emerald-500/40' : 'border-amber/40';
  const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
  const labelCls = isEm ? 'text-emerald-300' : 'text-amber';
  return (
    <div className={`mt-5 rounded-lg border ${borderCls} ${bgCls} p-5`}>
      <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
        {label}
      </p>
      <p className="mt-3 text-text/90">{body}</p>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'The brief',
    'The chain',
    '1 · Research',
    '2 · Draft',
    '3 · Audit',
    '4 · Ship',
    'Carry forward',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
